import React, { useState, useEffect } from 'react'
import {
  MagnifyingGlassIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'

const AUDIT_LEVELS = {
  INFO: { label: 'Info', color: 'bg-blue-100 text-blue-800', icon: CheckCircleIcon },
  WARNING: { label: 'Warning', color: 'bg-yellow-100 text-yellow-800', icon: ExclamationTriangleIcon },
  ERROR: { label: 'Error', color: 'bg-red-100 text-red-800', icon: ExclamationTriangleIcon },
  CRITICAL: { label: 'Critical', color: 'bg-red-200 text-red-900', icon: ExclamationTriangleIcon }
}

function AuditTrail() {
  const [auditLogs, setAuditLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [levelFilter, setLevelFilter] = useState('ALL')
  const [categoryFilter, setCategoryFilter] = useState('ALL')

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        const params = new URLSearchParams()
        if (levelFilter !== 'ALL') params.set('level', levelFilter)
        if (categoryFilter !== 'ALL') params.set('category', categoryFilter)
        const qs = params.toString()
        const url = getLocalApiUrl(`/api/audit-trail${qs ? '?' + qs : ''}`)
        const res = await authFetch(url)
        const json = await res.json()
        if (json.success) {
          setAuditLogs(json.data)
        }
      } catch (err) {
        console.error('Failed to fetch audit trail:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchAuditLogs()
  }, [levelFilter, categoryFilter])

  const q = searchTerm.trim().toLowerCase()
  const filteredLogs = auditLogs.filter((log) => {
    const matchesSearch =
      q === '' ||
      String(log.event || '')
        .toLowerCase()
        .includes(q) ||
      String(log.user || '')
        .toLowerCase()
        .includes(q) ||
      String(log.resource || '')
        .toLowerCase()
        .includes(q)
    const matchesLevel = levelFilter === 'ALL' || log.level === levelFilter
    const matchesCategory = categoryFilter === 'ALL' || log.category === categoryFilter

    return matchesSearch && matchesLevel && matchesCategory
  })

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString()
  }

  const categories = [...new Set(auditLogs.map((log) => log.category).filter(Boolean))]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Audit Trail</h1>
        <p className="text-gray-600">
          Complete audit trail of system events, security logs, and compliance tracking.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Logs
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by event, user, or resource..."
                className="input-field pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Level
            </label>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="select-field"
            >
              <option value="ALL">All Levels</option>
              {Object.entries(AUDIT_LEVELS).map(([key, level]) => (
                <option key={key} value={key}>{level.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="select-field"
            >
              <option value="ALL">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Audit Logs */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Audit Logs ({filteredLogs.length})
            </h2>
            <button className="btn-secondary">
              Export Audit Log
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredLogs.map((log) => {
            const LevelIcon = AUDIT_LEVELS[log.level].icon
            return (
              <div key={log.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${AUDIT_LEVELS[log.level].color.replace('text-', 'bg-').replace('-800', '-200').replace('-900', '-200')}`}>
                      <LevelIcon className="w-5 h-5" />
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${AUDIT_LEVELS[log.level].color}`}>
                          {AUDIT_LEVELS[log.level].label}
                        </span>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">
                          {log.category.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        {formatTimestamp(log.timestamp)}
                      </div>
                    </div>

                    <h3 className="text-sm font-medium text-gray-900 mb-2">
                      {log.event}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                      <div>
                        <span className="font-medium">User:</span> {log.user}
                      </div>
                      <div>
                        <span className="font-medium">IP:</span> {log.ip_address}
                      </div>
                      <div>
                        <span className="font-medium">Resource:</span> {log.resource}
                      </div>
                    </div>

                    {log.details && (
                      <div className="mt-3">
                        <details className="group">
                          <summary className="flex items-center cursor-pointer text-sm font-medium text-primary-600 hover:text-primary-500">
                            <DocumentMagnifyingGlassIcon className="w-4 h-4 mr-1" />
                            View Details
                          </summary>
                          <div className="mt-2 p-3 bg-gray-50 rounded-md">
                            <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default AuditTrail